import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BuildingOfficeIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon, GlobeAltIcon, Squares2X2Icon, ListBulletIcon, ArrowRightOnRectangleIcon, LifebuoyIcon } from '@heroicons/react/24/outline';
import {
  MODAL_BACKDROP,
  MODAL_BODY,
  MODAL_CLOSE_X,
  MODAL_HEAD,
  MODAL_ICON_TILE,
  MODAL_KICKER,
  MODAL_PANEL,
  MODAL_TITLE,
} from '@components/ui/designTokens';
import { cn } from '@lib/utils/cn';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { companyService } from '../services/companyService';
import { CompanyCard } from './CompanyCard';
import { useDebounce } from '@hooks/useDebounce';
import { useAuthStore } from '@features/auth/store/authStore';
import { useAuth } from '@features/auth/hooks/useAuth';
import { getDefaultRouteForRole } from '@lib/utils/roleNavigation';
import { BrandLogo } from '@components/common/BrandLogo';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 12;

export function CompanySelectorPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { logout } = useAuth();
  const searchRef = useRef<HTMLInputElement>(null);

  // Solo super admin puede ver esta página
  // Si no es super admin, redirigir siempre
  const isSuperAdmin = user?.isSuperAdmin === true;

  useEffect(() => {
    if (user && !isSuperAdmin) {
      const defaultRoute = getDefaultRouteForRole(user?.role);
      navigate(defaultRoute, { replace: true });
    }
  }, [navigate, user, isSuperAdmin]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchText, setSearchText] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const debouncedSearch = useDebounce(searchText, 300);

  // Search companies with pagination — disabled if not super admin
  const {
    data: companiesData,
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: ['companies', 'search', page, pageSize, debouncedSearch],
    queryFn: () => companyService.searchCompanies(page, pageSize, debouncedSearch || undefined),
    staleTime: 30 * 1000, // 30 seconds
    placeholderData: (previousData) => previousData,
    enabled: isSuperAdmin,
  });

  // Select company mutation
  const selectCompanyMutation = useMutation({
    mutationFn: (companyId: string) => companyService.selectCompany(companyId),
    onSuccess: async () => {
      // Clear all company-specific cache to prevent stale data between companies
      queryClient.clear();
      localStorage.removeItem('invoice-filter-storage');

      // Full page reload to ensure all state (React Query cache, auth store, etc.)
      // is fresh for the new company. navigate() caused race conditions where
      // queries fired before Redis was fully updated with the new company.
      const defaultRoute = getDefaultRouteForRole(user?.role);
      window.location.href = defaultRoute;
    },
    onError: (error: Error) => {
      console.error('Failed to select company:', error);
    },
  });

  const companies = companiesData?.companies || [];
  const totalPages = companiesData?.totalPages || 1;
  const total = companiesData?.total || 0;

  // Fija la compañía marcada como DefaultCompany (registro Aludra del usuario) al
  // inicio de la lista — el backend solo la manda en página 1 sin búsqueda activa
  // (ver authService.searchCompanies), mismo criterio que ya usa TenantSwitcher.tsx
  // con la compañía seleccionada.
  const defaultCompany = companiesData?.defaultCompany;
  const displayCompanies = defaultCompany
    ? [defaultCompany, ...companies.filter((c) => c.id !== defaultCompany.id)]
    : companies;

  /* SUBSCRIPTIONS_DISABLED
  const companiesForStatus = companies.filter(c => c.id && c.code).map(c => ({ id: c.id, code: c.code! }));
  const { data: subscriptionStatuses } = useQuery({
    queryKey: ['subscription-statuses', companiesForStatus.map(c => c.id).join(',')],
    queryFn: () => companyService.getCompaniesSubscriptionStatuses(companiesForStatus),
    enabled: companiesForStatus.length > 0,
    staleTime: 60 * 1000,
  });
  */

  // Auto-select if only one company and no search active
  useEffect(() => {
    if (companies.length === 1 && !debouncedSearch && total === 1 && !selectCompanyMutation.isPending) {
      console.log('Auto-selecting single company:', companies[0].id);
      selectCompanyMutation.mutate(companies[0].id);
    }
  }, [companies.length, total, debouncedSearch, selectCompanyMutation.isPending]);

  // Reset page when search or pageSize changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageSize]);

  /**
   * El diseño muestra un hint "⌘K" junto al buscador. Se implementa de verdad
   * en vez de dibujar un atajo que no hace nada.
   */
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleSelectCompany = useCallback((companyId: string) => {
    selectCompanyMutation.mutate(companyId);
  }, [selectCompanyMutation]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
  }, []);

  const goToPage = useCallback((newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  }, [totalPages]);

  // No renderizar nada si no es super admin (el useEffect se encarga de redirigir)
  if (!isSuperAdmin) {
    return null;
  }

  if (isLoading && !companiesData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando empresas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <BuildingOfficeIcon className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Error al cargar empresas</h2>
          <p className="mt-2 text-gray-600">
            No se pudieron obtener las empresas. Por favor, intenta nuevamente.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500" data-testid="company-selector-retry-button"
          >
            Reintentar
          </button>
          <button data-testid="company-selector-back-to-login-error-button"
            onClick={() => navigate('/login')}
            className="mt-2 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Volver al Login
          </button>
        </div>
      </div>
    );
  }

  if (total === 0 && !debouncedSearch) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
            <BuildingOfficeIcon className="h-6 w-6 text-yellow-600" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Sin empresas asignadas</h2>
          <p className="mt-2 text-gray-600">
            Tu usuario no tiene empresas asignadas. Por favor, contacta al administrador.
          </p>
          <button data-testid="company-selector-back-to-login-empty-button"
            onClick={() => navigate('/login')}
            className="mt-6 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Volver al Login
          </button>
        </div>
      </div>
    );
  }

  // If only one company and auto-selecting, show loading
  if (companies.length === 1 && !debouncedSearch && total === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Configurando empresa...</p>
          <p className="mt-2 text-sm text-gray-500">{companies[0].name}</p>
        </div>
      </div>
    );
  }

  /** Números de página a mostrar: 1 2 3 … último, con elipsis. */
  const pageNumbers = (() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const around = [page - 1, page, page + 1].filter((n) => n > 1 && n < totalPages);
    const set = new Set<number>([1, ...around, totalPages]);
    const sorted = [...set].sort((a, b) => a - b);
    const out: Array<number | 'gap'> = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push('gap');
      out.push(n);
    });
    return out;
  })();

  const NAV_ARROW =
    'inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border border-[#ececec] bg-white text-ink-850 shadow-[0_8px_20px_-8px_rgba(10,34,51,.20)] transition-[transform,border-color,color] hover:scale-105 hover:border-teal-500 hover:text-[#0d9488] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100 dark:border-secondary-700 dark:bg-secondary-800 dark:text-white';

  // Multiple companies - show selector with search and pagination
  return (
    <div className="relative min-h-screen bg-[#fafafa] font-display dark:bg-secondary-900">
      {/* Fondo ambiental */}
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <div
          className="absolute -right-[10%] -top-[20%] h-[700px] w-[700px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(18,196,178,.10), transparent 60%)' }}
        />
        <div
          className="absolute -bottom-[30%] -left-[5%] h-[600px] w-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(107,35,158,.08), transparent 60%)' }}
        />
      </div>

      {/* ============= TOPBAR ============= */}
      <header className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-4 border-b border-[#eeeeef] bg-white/80 px-6 py-3.5 backdrop-blur-xl sm:px-10 dark:border-secondary-700 dark:bg-secondary-900/80">
        <div className="flex items-center gap-3">
          <BrandLogo markSize={30} showWordmark={false} />
          <div className="leading-tight">
            <div className="text-[14px] font-extrabold tracking-[-0.01em] text-ink-850 dark:text-white">
              HyperNova Labs
            </div>
            <div className="font-mono text-[11px] tracking-[0.10em] text-secondary-400">
              FACTURACIÓN ELECTRÓNICA
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3.5">
          <div className="flex items-center gap-2 text-[13px] text-secondary-500">
            <span className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-full bg-ink-850 text-xs font-bold text-white">
              {(user?.firstName?.[0] ?? '') + (user?.lastName?.[0] ?? '') || '—'}
            </span>
            <span className="hidden sm:inline">
              <b className="text-ink-850 dark:text-white">
                {user?.fullName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
              </b>
              {user?.role && ` · ${user.role}`}
            </span>
          </div>
          <span className="h-6 w-px bg-[#ececec] dark:bg-secondary-700" />
          <button
            type="button"
            onClick={() => logout()}
            className="inline-flex items-center gap-1.5 rounded px-2 py-1.5 text-[13px] text-secondary-500 transition-colors hover:text-ink-850 dark:hover:text-white"
          >
            <ArrowRightOnRectangleIcon className="h-3.5 w-3.5" />
            Cerrar sesión
          </button>
        </div>
      </header>

      <main className="relative z-[1] mx-auto w-full max-w-[1440px] px-6 pb-12 pt-8 short:pb-6 short:pt-5 sm:px-10 sm:pt-10">
        {/* ============= ENCABEZADO: título + controles ============= */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3.5">
            <span aria-hidden="true" className="hidden h-7 w-1 flex-shrink-0 rounded-full bg-gradient-to-b from-teal-500 to-[#6b239e] sm:block" />
            <h1 className="text-[clamp(22px,2.4vw,28px)] font-extrabold leading-[1.05] tracking-[-0.03em] text-ink-850 dark:text-white">
              Selecciona una{' '}
              <span className="bg-gradient-to-br from-teal-500 to-[#6b239e] bg-clip-text text-transparent">
                Empresa
              </span>
            </h1>
            <span aria-hidden="true" className="hidden h-6 w-px bg-[#ececec] sm:block dark:bg-secondary-700" />
            <p className="text-[13px] text-secondary-500 dark:text-secondary-400">
              Tienes acceso a{' '}
              <b className="rounded-full bg-teal-500/10 px-2.5 py-0.5 font-mono font-bold text-ink-850 dark:text-white">
                {total}
              </b>{' '}
              {total === 1 ? 'empresa' : 'empresas'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3.5">
            <div className="relative rounded-[14px] border border-[#ececec] bg-white transition-[border-color,box-shadow] focus-within:border-[#0d9488] focus-within:ring-[3px] focus-within:ring-teal-500/10 dark:border-secondary-700 dark:bg-secondary-800">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-[18px] top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-secondary-400" />
              <input
                ref={searchRef}
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Buscar empresa por nombre, RUC o código…"
                className="w-full rounded-[14px] border-0 bg-transparent py-3.5 pl-[50px] pr-10 text-[15px] text-ink-850 outline-none placeholder:text-secondary-400 dark:text-white sm:w-[380px] md:w-[440px]"
               data-testid="company-selector-search-input"/>
              {isFetching && (
                <span className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin rounded-full border-b-2 border-[#0d9488]" />
              )}
            </div>

            {/* Vista */}
            <div className="inline-flex rounded-[10px] border border-[#ececec] bg-white p-1 dark:border-secondary-700 dark:bg-secondary-800">
              <button
                onClick={() => setViewMode('grid')}
                title="Vista de tarjetas"
                aria-label="Vista cuadrícula"
                aria-pressed={viewMode === 'grid'}
                className={`inline-flex items-center justify-center rounded-[7px] px-2.5 py-2 transition-colors ${
                  viewMode === 'grid' ? 'bg-ink-850 text-white' : 'text-secondary-400 hover:text-ink-850 dark:hover:text-white'
                }`} data-testid="company-selector-view-grid-button"
              >
                <Squares2X2Icon className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                title="Vista de lista"
                aria-label="Vista lista"
                aria-pressed={viewMode === 'list'}
                className={`inline-flex items-center justify-center rounded-[7px] px-2.5 py-2 transition-colors ${
                  viewMode === 'list' ? 'bg-ink-850 text-white' : 'text-secondary-400 hover:text-ink-850 dark:hover:text-white'
                }`} data-testid="company-selector-view-list-button"
              >
                <ListBulletIcon className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Cantidad por página */}
            <div className="inline-flex items-center gap-2 text-[13px] text-secondary-500">
              <label htmlFor="pageSize" className="whitespace-nowrap">Mostrar</label>
              <div className="relative inline-flex">
                <select
                  id="pageSize"
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="min-w-[72px] cursor-pointer appearance-none rounded-lg border border-[#ececec] bg-white py-2 pl-3 pr-8 font-mono text-[13px] font-bold text-ink-850 outline-none dark:border-secondary-700 dark:bg-secondary-800 dark:text-white" data-testid="company-selector-page-size-select"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <svg
                  aria-hidden="true"
                  className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-secondary-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* ============= RESULTADOS ============= */}
        {companies.length > 0 ? (
          <>
            {viewMode === 'grid' ? (
              <div className="mt-6 grid grid-cols-1 gap-[18px] short:mt-4 short:gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {displayCompanies.map((company) => (
                  <CompanyCard
                    key={company.id}
                    company={company}
                    isDefault={company.id === defaultCompany?.id}
                    isSelecting={selectCompanyMutation.isPending}
                    onSelect={handleSelectCompany}
                  />
                ))}
              </div>
            ) : (
              <div className="mt-6 overflow-hidden rounded-2xl border border-[#ececec] bg-white shadow-[0_1px_2px_rgba(10,34,51,.03),0_10px_24px_-14px_rgba(10,34,51,.12)] dark:border-secondary-700 dark:bg-secondary-800">
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse text-[14px]" data-testid="company-selector-table">
                    <thead>
                      <tr>
                        {['Empresa', 'RUC', 'Código', 'Acción'].map((h, i) => (
                          <th
                            key={h}
                            className={`whitespace-nowrap border-b border-[#ececec] bg-secondary-50 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-secondary-400 dark:border-secondary-700 dark:bg-secondary-900 ${
                              i === 3 ? 'text-right' : 'text-left'
                            } ${i === 1 ? 'hidden sm:table-cell' : ''} ${i === 2 ? 'hidden md:table-cell' : ''}`}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {displayCompanies.map((company) => {
                        return (
                          <tr
                            data-testid={`company-selector-table-row-${company.id}`}
                            key={company.id}
                            className="cursor-pointer border-b border-[#f4f4f4] transition-colors last:border-0 hover:bg-[#fafbfc] dark:border-secondary-700 dark:hover:bg-secondary-700/40"
                            onClick={() => !selectCompanyMutation.isPending && handleSelectCompany(company.id)}
                          >
                            <td className="whitespace-nowrap px-6 py-3">
                              <div className="flex items-center gap-3">
                                {company.image ? (
                                  <img src={company.image} alt={company.name} className="h-8 w-8 rounded-md object-contain bg-white" />
                                ) : (
                                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-teal-500 to-[#6b239e]">
                                    <BuildingOfficeIcon className="h-4 w-4 text-white" />
                                  </div>
                                )}
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-ink-850 dark:text-white">{company.name}</span>
                                    {company.id === defaultCompany?.id && (
                                      <span className="inline-flex flex-shrink-0 items-center rounded-full bg-teal-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#0d9488]">
                                        Predeterminada
                                      </span>
                                    )}
                                  </div>
                                  {company.email && (
                                    <div className="text-[12px] text-secondary-400">{company.email}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="hidden whitespace-nowrap px-6 py-3 font-mono text-secondary-500 sm:table-cell">
                              {company.ruc || '—'}
                            </td>
                            <td className="hidden whitespace-nowrap px-6 py-3 font-mono text-secondary-500 md:table-cell">
                              {company.code || '—'}
                            </td>
                            <td className="whitespace-nowrap px-6 py-3 text-right">
                              <button
                                data-testid={`company-selector-select-button-${company.id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectCompany(company.id);
                                }}
                                disabled={selectCompanyMutation.isPending}
                                className="text-[13px] font-semibold text-[#0d9488] transition-colors hover:text-[#6b239e] disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Seleccionar
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="py-12 text-center" data-testid="company-selector-no-results">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-ink-850 dark:text-white">
              No se encontraron empresas
            </h3>
            <p className="mt-1 text-sm text-secondary-500">Intenta con otro término de búsqueda.</p>
          </div>
        )}

        {/* ============= PAGINACIÓN ============= */}
        {totalPages > 1 && (
          <div className="mt-10 flex flex-wrap items-center justify-between gap-4 short:mt-5">
            <p className="text-left text-[12px] text-secondary-400">
              Mostrando{' '}
              <b className="font-semibold text-secondary-500 dark:text-secondary-300">
                {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)}
              </b>{' '}
              de <b className="font-semibold text-secondary-500 dark:text-secondary-300">{total}</b>{' '}
              {total === 1 ? 'empresa' : 'empresas'} · Página {page} de {totalPages}
            </p>
            <div className="flex items-center gap-5">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page === 1}
                aria-label="Anterior"
                className={NAV_ARROW} data-testid="company-selector-prev-page-button"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <div className="inline-flex items-center gap-1">
                {pageNumbers.map((n, i) =>
                  n === 'gap' ? (
                    <span key={`gap-${i}`} className="w-6 text-center font-mono text-secondary-300">
                      ···
                    </span>
                  ) : (
                    <button
                      key={n}
                      onClick={() => goToPage(n)}
                      aria-current={n === page ? 'page' : undefined}
                      className={`h-9 min-w-[36px] rounded-lg px-2 font-mono text-[13px] transition-colors ${
                        n === page
                          ? 'bg-ink-850 font-bold text-white'
                          : 'font-semibold text-secondary-500 hover:bg-white hover:text-ink-850 dark:hover:bg-secondary-800 dark:hover:text-white'
                      }`}
                    >
                      {n}
                    </button>
                  )
                )}
              </div>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page === totalPages}
                aria-label="Siguiente"
                className={NAV_ARROW} data-testid="company-selector-next-page-button"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Pie — divisor con el gradiente de marca en vez de una línea gris
            plana, pero muy tenue: es un cierre de página, no un
            protagonista. */}
        <div className="mt-10 short:mt-5">
          <div
            aria-hidden="true"
            className="h-px w-full bg-gradient-to-r from-teal-500/25 via-[#6b239e]/25 to-transparent"
          />
          <div className="mt-4 short:mt-2">
            <p className="text-center text-sm text-secondary-400">
              ¿No ves tu empresa?{' '}
              <button
                onClick={() => setIsContactModalOpen(true)}
                className="font-semibold text-[#0d9488] transition-colors hover:text-[#6b239e]" data-testid="company-selector-contact-admin-button"
              >
                Contacta al administrador
              </button>
            </p>
          </div>
        </div>
      </main>

      {/* Contact Modal */}
      {isContactModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className={MODAL_BACKDROP}
            onClick={() => setIsContactModalOpen(false)}
           data-testid="company-selector-contact-modal-overlay"/>
          <div className={cn(MODAL_PANEL, 'relative mx-4 max-w-md')}>
            <div className={MODAL_HEAD}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3.5">
                  <span className={MODAL_ICON_TILE}>
                    <LifebuoyIcon />
                  </span>
                  <div className="min-w-0">
                    <div className={MODAL_KICKER}>Soporte · Ayuda</div>
                    <h3 className={cn(MODAL_TITLE, 'mt-1')}>Contactar Soporte</h3>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsContactModalOpen(false)}
                  aria-label="Cerrar"
                  className={MODAL_CLOSE_X} data-testid="company-selector-contact-modal-close-button"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className={MODAL_BODY}>
              <p className="text-[14px] leading-[1.55] text-secondary-500">
                Visita nuestro portal de soporte para obtener ayuda:
              </p>

              {/* Portal de Soporte */}
              <a
                href="https://support.hypernovalabs.com/"
                data-testid="company-selector-contact-modal-support-link"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 flex items-center gap-3 rounded-xl border border-[#eef0f2] bg-[#fbfbfc] p-4 transition-colors hover:border-teal-500/30 hover:bg-teal-500/[.04]"
              >
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-teal-500/10 text-[#0d9488]">
                  <GlobeAltIcon className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-bold text-ink-850">Portal de Soporte</p>
                  <p className="font-mono text-[12px] text-[#0d9488]">support.hypernovalabs.com</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
